import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import PrivacyPage from "@/pages/PrivacyPage";
import DashboardPage from "@/pages/DashboardPage";
import MessagesPage from "@/pages/MessagesPage";
import ContactsPage from "@/pages/ContactsPage";
import SettingsPage from "@/pages/SettingsPage";
import VerificationPendingPage from "@/pages/VerificationPendingPage";
import AdminVerificationsPage from "@/pages/AdminVerificationsPage";
import AdminAuditLogPage from "@/pages/AdminAuditLogPage";
import AdminStatsPage from "@/pages/AdminStatsPage";
import SuggestionsPage from "@/pages/SuggestionsPage";
import NuovoRefertoPage from "@/pages/NuovoRefertoPage";
import StoricoRefertiPage from "@/pages/StoricoRefertiPage";
import CartellaPazientiPage from "@/pages/CartellaPazientiPage";
import CartellaPazientePage from "@/pages/CartellaPazientePage";
import ModuloPazientePage from "@/pages/ModuloPazientePage";
import NuovaValutazionePage from "@/pages/NuovaValutazionePage";
import CasiCliniciPage from "@/pages/CasiCliniciPage";
import CasoDetailPage from "@/pages/CasoDetailPage";
import Layout from "@/components/Layout";
import SynapseLoader from "@/components/SynapseLoader";
import PWASplashScreen from "@/components/PWASplashScreen";
import "@/App.css";

function Loading() {
  return (
    <div className="flex h-screen items-center justify-center bg-[#020615]">
      <SynapseLoader size="xl" label="Sto attivando le sinapsi…" />
      <span data-testid="app-loading" className="sr-only">Caricamento</span>
    </div>
  );
}

/** Utenti autenticati con verifica in stato non-verified vengono dirottati su /verifica-in-corso */
function VerifiedGate({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  const status = user.verification_status;
  if (!user.is_admin && status !== "verified") {
    return <Navigate to="/verifica-in-corso" replace />;
  }
  return <Layout>{children}</Layout>;
}

function PendingOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;
  return <Layout>{children}</Layout>;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    if (!user.is_admin && user.verification_status !== "verified") {
      return <Navigate to="/verifica-in-corso" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <PWASplashScreen />
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><RegisterPage /></PublicOnly>} />
          <Route path="/forgot-password" element={<PublicOnly><ForgotPasswordPage /></PublicOnly>} />
          <Route path="/reset-password/:token" element={<PublicOnly><ResetPasswordPage /></PublicOnly>} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/verifica-in-corso" element={<PendingOnly><VerificationPendingPage /></PendingOnly>} />

          <Route path="/dashboard" element={<VerifiedGate><DashboardPage /></VerifiedGate>} />
          <Route path="/messaggi" element={<VerifiedGate><MessagesPage /></VerifiedGate>} />
          <Route path="/messaggi/:threadId" element={<VerifiedGate><MessagesPage /></VerifiedGate>} />
          <Route path="/rubrica" element={<VerifiedGate><ContactsPage /></VerifiedGate>} />
          <Route path="/impostazioni" element={<VerifiedGate><SettingsPage /></VerifiedGate>} />
          <Route path="/suggerimenti" element={<VerifiedGate><SuggestionsPage /></VerifiedGate>} />
          <Route path="/nuovo-referto" element={<VerifiedGate><NuovoRefertoPage /></VerifiedGate>} />
          <Route path="/storico-referti" element={<VerifiedGate><StoricoRefertiPage /></VerifiedGate>} />
          <Route path="/cartelle-pazienti" element={<VerifiedGate><CartellaPazientiPage /></VerifiedGate>} />
          <Route path="/cartella-paziente/:cf" element={<VerifiedGate><CartellaPazientePage /></VerifiedGate>} />
          <Route path="/modulo/:token" element={<ModuloPazientePage />} />
          <Route path="/nuova-valutazione" element={<VerifiedGate><NuovaValutazionePage /></VerifiedGate>} />
          <Route path="/casi" element={<VerifiedGate><CasiCliniciPage /></VerifiedGate>} />
          <Route path="/casi/:caseId" element={<VerifiedGate><CasoDetailPage /></VerifiedGate>} />

          <Route path="/admin/statistiche" element={<AdminOnly><AdminStatsPage /></AdminOnly>} />
          <Route path="/admin/verifiche" element={<AdminOnly><AdminVerificationsPage /></AdminOnly>} />
          <Route path="/admin/audit" element={<AdminOnly><AdminAuditLogPage /></AdminOnly>} />

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
